import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Home.css';

interface ScreenshotSet {
  title: string;
  images: string[];
  description: string;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedScreenshot, setSelectedScreenshot] = useState<ScreenshotSet | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [bannerImages, setBannerImages] = useState<string[]>([]);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const galleryRef = useRef<HTMLDivElement>(null);
  const costSavingsRef = useRef<HTMLDivElement>(null);
  const howItWorksRef = useRef<HTMLDivElement>(null);
  const whoIsThisForRef = useRef<HTMLDivElement>(null);
  const seeItInActionRef = useRef<HTMLDivElement>(null);
  const [isGalleryVisible, setIsGalleryVisible] = useState(false);
  const [isCostVisible, setIsCostVisible] = useState(false);
  const [isHowItWorksVisible, setIsHowItWorksVisible] = useState(false);
  const [isWhoIsThisForVisible, setIsWhoIsThisForVisible] = useState(false);
  const [isSeeItInActionVisible, setIsSeeItInActionVisible] = useState(false);
  const [currentBookIndex, setCurrentBookIndex] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [autoPlayScreenshots, setAutoPlayScreenshots] = useState(true);
  const [screenshots] = useState<{ [key: string]: ScreenshotSet }>({
    'plan-story': {
      title: 'Plan Your Story',
      images: [
        '/screenshots/plan-your-story/plan1.jpeg',
        '/screenshots/plan-your-story/plan2.png'
      ],
      description: 'Use our AI plot planner to outline your story structure and generate page-by-page narratives with intelligent suggestions.'
    },
    'generate-characters': {
      title: 'Generate Characters',
      images: [
        '/screenshots/generate-characters/char1.png',
        '/screenshots/generate-characters/char2.png',
        '/screenshots/generate-characters/char3.png'
      ],
      description: 'Create unique AI-generated characters with custom descriptions and consistent appearances across all pages.'
    },
    'design-pages': {
      title: 'Design Pages',
      images: [
        '/screenshots/design-pages/design.png',
        '/screenshots/design-pages/design2.png',
        '/screenshots/design-pages/design3.png',
        '/screenshots/design-pages/design4.png'
      ],
      description: 'Use our intuitive drag-and-drop editor to arrange text and images on your storybook pages with precision.'
    },
    'publish-amazon': {
      title: 'Publish on Amazon',
      images: [
        '/screenshots/publish-amazon/publish1.jpeg',
        '/screenshots/publish-amazon/publish2.png'
      ],
      description: 'Export your completed storybook in print-ready format and easily publish it on Amazon KDP.'
    }
  });

  // Load banner images on mount
  useEffect(() => {
    // Define banner images - dynamically include all available banners
    const banners = [
      '/screenshots/banner/banner1.png',
      '/screenshots/banner/banner2.png',
      '/screenshots/banner/banner3.png',
      '/screenshots/banner/banner4.png',
      '/screenshots/banner/banner5.png',
      '/screenshots/banner/banner6.png',
      '/screenshots/banner/banner7.png',
      '/screenshots/banner/banner8.png',
      '/screenshots/banner/banner9.png',
      '/screenshots/banner/banner10.png',
      '/screenshots/banner/banner11.png',
      '/screenshots/banner/banner13.png',
      '/screenshots/banner/banner14.png',
      '/screenshots/banner/banner15.png',
      '/screenshots/banner/banner16.png',
      '/screenshots/banner/banner17.png'
    ];

    setBannerImages(banners);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const galleryObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsGalleryVisible(true);
        }
      });
    }, observerOptions);

    const costObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsCostVisible(true);
        }
      });
    }, observerOptions);

    const howItWorksObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsHowItWorksVisible(true);
        }
      });
    }, observerOptions);

    const whoIsThisForObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsWhoIsThisForVisible(true);
        }
      });
    }, observerOptions);

    const seeItInActionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsSeeItInActionVisible(true);
        }
      });
    }, observerOptions);

    if (galleryRef.current) {
      galleryObserver.observe(galleryRef.current);
    }
    if (costSavingsRef.current) {
      costObserver.observe(costSavingsRef.current);
    }
    if (howItWorksRef.current) {
      howItWorksObserver.observe(howItWorksRef.current);
    }
    if (whoIsThisForRef.current) {
      whoIsThisForObserver.observe(whoIsThisForRef.current);
    }
    if (seeItInActionRef.current) {
      seeItInActionObserver.observe(seeItInActionRef.current);
    }

    return () => {
      galleryObserver.disconnect();
      costObserver.disconnect();
      howItWorksObserver.disconnect();
      whoIsThisForObserver.disconnect();
      seeItInActionObserver.disconnect();
    };
  }, []);

  // Book flipping animation for gallery
  useEffect(() => {
    if (!isGalleryVisible) return;

    const interval = setInterval(() => {
      setCurrentBookIndex((prevIndex) => {
        const gallery = galleryRef.current?.querySelector('.gallery-scroll') as HTMLElement;
        if (gallery) {
          const totalBooks = gallery.children.length;
          return (prevIndex + 1) % totalBooks;
        }
        return prevIndex;
      });
    }, 3000); // Flip every 3 seconds

    return () => clearInterval(interval);
  }, [isGalleryVisible]);

  // Auto-play screenshots in See It In Action section
  useEffect(() => {
    if (!isSeeItInActionVisible || !autoPlayScreenshots) return;

    const stepKeys = ['plan-story', 'generate-characters', 'design-pages', 'publish-amazon'];
    const interval = setInterval(() => {
      setCurrentStepIndex((prevIndex) => (prevIndex + 1) % stepKeys.length);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [isSeeItInActionVisible, autoPlayScreenshots]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxImage) {
        if (e.key === 'Escape') {
          closeLightbox();
        } else if (e.key === 'ArrowLeft') {
          prevLightboxImage();
        } else if (e.key === 'ArrowRight') {
          nextLightboxImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, lightboxIndex]);
  
  const openScreenshot = (key: string) => {
    setSelectedScreenshot(screenshots[key]);
    setCurrentImageIndex(0);
  };
  
  const closeModal = () => {
    setSelectedScreenshot(null);
    setCurrentImageIndex(0);
  };
  
  const nextImage = () => {
    if (selectedScreenshot && currentImageIndex < selectedScreenshot.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };
  
  const prevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const openLightbox = (imageUrl: string, index: number) => {
    setLightboxImage(imageUrl);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const nextLightboxImage = () => {
    const bannerNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 13];
    const totalImages = bannerNumbers.length;
    const nextIndex = (lightboxIndex + 1) % totalImages;
    setLightboxIndex(nextIndex);
    setLightboxImage(`/screenshots/banner/banner${bannerNumbers[nextIndex]}.png`);
  };

  const prevLightboxImage = () => {
    const bannerNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 13];
    const totalImages = bannerNumbers.length;
    const prevIndex = (lightboxIndex - 1 + totalImages) % totalImages;
    setLightboxIndex(prevIndex);
    setLightboxImage(`/screenshots/banner/banner${bannerNumbers[prevIndex]}.png`);
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        {bannerImages.length > 0 && (
          <div className="hero-banner-slider">
            {/* Duplicate images for seamless loop */}
            {[...bannerImages, ...bannerImages].map((img, index) => (
              <div
                key={index}
                className="hero-banner-image"
                style={{ backgroundImage: `url(${img})` }}
              />
            ))}
          </div>
        )}
        <div className="hero-content">
          <h1 className="hero-title">
            <img src="/banner-icon.png" alt="Tiny Little Fox Studio" className="banner-icon" />
            Tiny Little Fox Studio
          </h1>
          <p className="hero-subtitle">
            The Only Platform That Creates Amazon KDP-Ready Storybooks in Hours, Not Months
          </p>
          <p className="hero-description">
            Transform your ideas into beautiful, professional storybooks with AI-powered illustrations. 
            Export in print-ready format and publish directly to Amazon KDP with our specialized publishing tools.
          </p>
          <div className="hero-badges">
            <span className="badge badge-primary">📚 Amazon KDP Ready</span>
            <span className="badge badge-primary">🎨 AI-Powered Design</span>
            <span className="badge badge-primary">⚡ Publish in Hours</span>
          </div>
          <div className="hero-buttons">
            {user ? (
              <button className="btn btn-primary btn-large" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </button>
            ) : (
              <>
                <button className="btn btn-primary btn-large" onClick={() => navigate('/register')}>
                  Get Started Free
                </button>
                <button className="btn btn-secondary btn-large" onClick={() => navigate('/login')}>
                  Login
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Video Introduction Section */}
      <section className="video-intro-section">
        <div className="video-container">
          <h2 className="video-title">See How It Works</h2>
          <div className="video-wrapper">
            <iframe
              src="https://www.youtube.com/embed/RwAmFGHvlYc?vq=hd1080&hd=1"
              title="Tiny Little Fox Studio - How It Works"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {/* Screenshot Gallery Section */}
      <section className="screenshot-gallery-section" ref={galleryRef}>
        <div className={`gallery-container ${isGalleryVisible ? 'animate-in' : ''}`}>
          <h2 className="gallery-title">See It In Action</h2>
          <p className="gallery-subtitle">Explore the powerful features that make creating storybooks effortless</p>
          
          <div className="book-flip-container">
            <div className="books-stack">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 14, 15, 16, 17].map((num, index) => {
                const isFlipped = currentBookIndex > index;
                const isCurrent = currentBookIndex === index;
                const isPrevious = index < currentBookIndex;
                
                // Calculate offset for stacked effect
                let offsetY = 0;
                let offsetX = 0;
                let scale = 1;
                
                if (isPrevious) {
                  // Flipped pages stack on the left with slight offset
                  const distanceFromCurrent = currentBookIndex - index;
                  offsetX = -distanceFromCurrent * 3;
                  offsetY = -distanceFromCurrent * 2;
                  scale = 1 - (distanceFromCurrent * 0.02);
                } else if (!isCurrent) {
                  // Unflipped pages stack on the right with slight offset
                  const distanceFromCurrent = index - currentBookIndex;
                  offsetX = distanceFromCurrent * 3;
                  offsetY = distanceFromCurrent * 2;
                  scale = 1 - (distanceFromCurrent * 0.02);
                }
                
                return (
                  <div 
                    key={num} 
                    className={`book-page ${isCurrent ? 'active' : ''} ${isPrevious ? 'flipped' : ''}`}
                    style={{ 
                      zIndex: isCurrent ? 100 : (isPrevious ? index : 50 - index),
                      transform: `
                        rotateY(${isFlipped ? -180 : 0}deg) 
                        translateX(${offsetX}px) 
                        translateY(${offsetY}px)
                        scale(${scale})
                      `,
                      opacity: Math.max(0.4, 1 - Math.abs(currentBookIndex - index) * 0.15)
                    }}
                    onClick={() => openLightbox(`/screenshots/banner/banner${num}.png`, index)}
                  >
                    <div className="book-page-inner">
                      <div className="book-page-front">
                        <img 
                          src={`/screenshots/banner/banner${num}.png`}
                          alt={`Screenshot ${num}`}
                          className="book-image"
                        />
                        <div className="page-number">{index + 1}</div>
                      </div>
                      <div className="book-page-back">
                        <img 
                          src={`/screenshots/banner/banner${num}.png`}
                          alt={`Screenshot ${num} back`}
                          className="book-image back-image"
                        />
                        <div className="page-number">{index + 1}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="book-controls">
              <button 
                className="book-nav-btn"
                onClick={() => setCurrentBookIndex((prev) => Math.max(0, prev - 1))}
                disabled={currentBookIndex === 0}
              >
                ← Previous
              </button>
              <span className="page-indicator">{currentBookIndex + 1} / 16</span>
              <button 
                className="book-nav-btn"
                onClick={() => setCurrentBookIndex((prev) => Math.min(15, prev + 1))}
                disabled={currentBookIndex === 15}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Savings Section */}
      <section className="cost-savings-section" ref={costSavingsRef}>
        <div className={`cost-savings-content ${isCostVisible ? 'animate-in' : ''}`}>
          <h2 className={isCostVisible ? 'slide-in-up' : ''}>💰 Create and Publish Your Book for 95% Less</h2>
          <div className="cost-comparison">
            <div className={`cost-card traditional ${isCostVisible ? 'slide-in-left' : ''}`}>
              <h3>Traditional Method</h3>
              <div className="cost-amount">$3,000 - $17,000+</div>
              <ul className="cost-breakdown">
                <li>Professional Illustrator: $2,000 - $10,000+</li>
                <li>Writer/Editor: $500 - $5,000</li>
                <li>Book Designer: $500 - $2,000</li>
                <li>Months of waiting</li>
              </ul>
            </div>
            <div className={`cost-divider ${isCostVisible ? 'scale-in' : ''}`}>
              <span className="vs-badge">VS</span>
            </div>
            <div className={`cost-card tinyfox ${isCostVisible ? 'slide-in-right' : ''}`}>
              <h3>Tiny Little Fox</h3>
              <div className="cost-amount highlight">~$20</div>
              <ul className="cost-breakdown">
                <li>✓ AI-Generated Illustrations</li>
                <li>✓ AI Story Planning</li>
                <li>✓ Professional Layout Tools</li>
                <li>✓ Ready in Hours, Not Months</li>
                <li>✓ 100% Copyright Ownership</li>
              </ul>
            </div>
          </div>
          <p className={`cost-savings-cta ${isCostVisible ? 'fade-in' : ''}`}>
            <strong>Save $2,980 - $16,980 per book!</strong> No professional illustrators, no expensive writers, no waiting. 
            Create professional-quality storybooks at a fraction of traditional costs.
          </p>
          <div className={`earnings-info ${isCostVisible ? 'slide-in-up' : ''}`}>
            <h3>💵 Start Earning From Your Books</h3>
            <p className="earnings-description">
              Self-publishers on Amazon KDP can earn royalties ranging from <strong>35% to 70%</strong> of the book's price. 
              Many successful authors earn <strong>$500 to $5,000+ per month</strong> from their published books, 
              with some top creators making <strong>$10,000+ monthly</strong>. Your earnings potential depends on your marketing, 
              book quality, and niche selection. Start with minimal investment and keep most of the profits!
            </p>
          </div>
        </div>
      </section>

      {/* Who Is This For Section */}
      <section className="audience-section" ref={whoIsThisForRef}>
        <h2 className={isWhoIsThisForVisible ? 'fade-in' : ''}>Who Is This For?</h2>
        <div className="audience-grid">
          <div className={`audience-card ${isWhoIsThisForVisible ? 'animate-in' : ''}`} style={{ animationDelay: '0s' }}>
            <div className="audience-icon">👨‍👩‍👧‍👦</div>
            <h3>Parents</h3>
            <p>
              Help your children bring their creative stories to life and publish them as real books. 
              Create lasting memories and boost your child's confidence by turning their imagination into a professional storybook.
            </p>
          </div>
          <div className={`audience-card ${isWhoIsThisForVisible ? 'animate-in' : ''}`} style={{ animationDelay: '0.15s' }}>
            <div className="audience-icon">💡</div>
            <h3>Creative Individuals</h3>
            <p>
              Have great story ideas but lack drawing skills or writing experience? 
              Our AI-powered tools help you create professional illustrations and refine your narrative without any artistic background.
            </p>
          </div>
          <div className={`audience-card ${isWhoIsThisForVisible ? 'animate-in' : ''}`} style={{ animationDelay: '0.3s' }}>
            <div className="audience-icon">⚡</div>
            <h3>Professionals</h3>
            <p>
              Speed up your creative process and bring your visions to life quickly. 
              Perfect for educators, content creators, and storytellers who need to produce quality books efficiently.
            </p>
          </div>
          <div className={`audience-card ${isWhoIsThisForVisible ? 'animate-in' : ''}`} style={{ animationDelay: '0.45s' }}>
            <div className="audience-icon">🏢</div>
            <h3>Small Businesses</h3>
            <p>
              Create engaging short stories for your marketing campaigns and social media. 
              Stand out with unique, branded storybooks that connect with your audience and tell your brand's story in a memorable way.
            </p>
          </div>
          <div className={`audience-card ${isWhoIsThisForVisible ? 'animate-in' : ''}`} style={{ animationDelay: '0.6s' }}>
            <div className="audience-icon">📱</div>
            <h3>Social Media Creators</h3>
            <p>
              Create captivating storybooks about your car, travel adventures, hobbies, or daily life for your social media content. 
              Transform your experiences into professional visual stories that engage your followers and grow your audience.
            </p>
          </div>
        </div>
      </section>

      {/* See It In Action - Auto-playing Screenshots */}
      <section className="see-it-in-action" ref={seeItInActionRef}>
        <h2 className={isSeeItInActionVisible ? 'fade-in' : ''}>See It In Action</h2>
        <p className="section-subtitle">Watch how easy it is to create professional storybooks</p>
        
        <div className={`action-demo-container ${isSeeItInActionVisible ? 'animate-in' : ''}`}>
          <div className="demo-steps">
            {['plan-story', 'generate-characters', 'design-pages', 'publish-amazon'].map((key, index) => {
              const stepInfo = {
                'plan-story': { num: 1, title: 'Plan Your Story', desc: 'AI-powered story planning and narrative generation' },
                'generate-characters': { num: 2, title: 'Generate Characters', desc: 'Create unique AI-generated character artwork' },
                'design-pages': { num: 3, title: 'Design Pages', desc: 'Drag-and-drop visual editor for layout' },
                'publish-amazon': { num: 4, title: 'Publish on Amazon KDP', desc: 'Export in Amazon KDP print-ready format' }
              }[key];
              
              return (
                <div 
                  key={key}
                  className={`demo-step ${currentStepIndex === index ? 'active' : ''}`}
                  onClick={() => { setCurrentStepIndex(index); setAutoPlayScreenshots(false); }}
                >
                  <div className="step-number">{stepInfo?.num}</div>
                  <div className="step-content">
                    <h3>{stepInfo?.title}</h3>
                    <p>{stepInfo?.desc}</p>
                  </div>
                  {currentStepIndex === index && <div className="active-indicator"></div>}
                </div>
              );
            })}
          </div>
          
          <div className="demo-preview">
            {['plan-story', 'generate-characters', 'design-pages', 'publish-amazon'].map((key, index) => (
              <div 
                key={key}
                className={`preview-slide ${currentStepIndex === index ? 'active' : ''}`}
              >
                {screenshots[key].images.map((img, imgIndex) => (
                  <img 
                    key={imgIndex}
                    src={img} 
                    alt={screenshots[key].title}
                    className={`preview-image ${imgIndex === 0 ? 'active' : ''}`}
                    onClick={() => openScreenshot(key)}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
        
        <div className="demo-controls">
          <button 
            className={`auto-play-btn ${autoPlayScreenshots ? 'active' : ''}`}
            onClick={() => setAutoPlayScreenshots(!autoPlayScreenshots)}
          >
            {autoPlayScreenshots ? '⏸ Pause' : '▶ Play'} Auto-Demo
          </button>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works" ref={howItWorksRef}>
        <h2 className={isHowItWorksVisible ? 'fade-in' : ''}>How It Works</h2>
        <div className="steps-grid">
          <div className={`step ${isHowItWorksVisible ? 'animate-in' : ''}`} 
               style={{ animationDelay: '0s' }}
               onClick={() => openScreenshot('plan-story')}>
            <div className="step-number">1</div>
            <h3>Plan Your Story</h3>
            <p>Use our AI plot planner to outline your story structure and generate page-by-page narratives.</p>
          </div>
          <div className={`step ${isHowItWorksVisible ? 'animate-in' : ''}`}
               style={{ animationDelay: '0.15s' }}
               onClick={() => openScreenshot('generate-characters')}>
            <div className="step-number">2</div>
            <h3>Generate Characters</h3>
            <p>Create unique AI-generated characters with custom descriptions and consistent appearances.</p>
          </div>
          <div className={`step ${isHowItWorksVisible ? 'animate-in' : ''}`}
               style={{ animationDelay: '0.3s' }}
               onClick={() => openScreenshot('design-pages')}>
            <div className="step-number">3</div>
            <h3>Design Pages</h3>
            <p>Use our drag-and-drop editor to arrange text and images on your storybook pages.</p>
          </div>
          <div className={`step ${isHowItWorksVisible ? 'animate-in' : ''}`}
               style={{ animationDelay: '0.45s' }}
               onClick={() => openScreenshot('publish-amazon')}>

            <div className="step-number">4</div>
            <h3>Export for Amazon KDP</h3>
            <p>One-click export in Amazon KDP's required format. Ready to publish without any technical knowledge.</p>
          </div>
        </div>
      </section>

      {/* Publishing Section - Enhanced */}
      <section className="publishing-section">
        <div className="publishing-content">
          <div className="publishing-badge">🎯 Amazon KDP Specialist</div>
          <h2>📚 The Only Platform Built for Amazon KDP Publishing</h2>
          <p className="publishing-intro">
            <strong>Tiny Little Fox Studio is the only AI storybook creator specifically designed for Amazon KDP.</strong> 
            We handle all the technical requirements so you can focus on your story. Export in Amazon's exact required format, 
            with proper dimensions, bleed settings, and print specifications—all automatically configured.
          </p>
          <div className="publishing-highlights">
            <div className="highlight-card">
              <div className="highlight-icon">📐</div>
              <h3>Perfect Dimensions</h3>
              <p>Automatic sizing for Amazon's most popular trim sizes (8.5" x 8.5", 8.5" x 11", and more)</p>
            </div>
            <div className="highlight-card">
              <div className="highlight-icon">🎨</div>
              <h3>Print-Ready PDFs</h3>
              <p>Exports include proper bleed margins, color profiles, and resolution required by KDP</p>
            </div>
            <div className="highlight-card">
              <div className="highlight-icon">✅</div>
              <h3>KDP Validation</h3>
              <p>Your files pass Amazon's quality checks the first time, every time</p>
            </div>
          </div>
          <div className="publishing-benefits">
            <div className="benefit">
              <span className="benefit-icon">✓</span>
              <span>One-click export in Amazon KDP format</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">✓</span>
              <span>No upfront costs or minimum orders</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">✓</span>
              <span>Earn 35-70% royalties from every sale</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">✓</span>
              <span>Reach 300+ million Amazon customers worldwide</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">✓</span>
              <span>Print-on-demand - no inventory or warehousing</span>
            </div>
            <div className="benefit">
              <span className="benefit-icon">✓</span>
              <span>Maintain 100% rights to your creative work</span>
            </div>
          </div>
          <div className="publishing-cta">
            <a 
              href="https://a.co/d/j7LOfDz" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-primary btn-large"
              style={{ marginRight: '16px' }}
            >
              📖 View Book Published on Amazon
            </a>
            <a 
              href="https://kdp.amazon.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-secondary btn-large"
            >
              Learn More About Amazon KDP
            </a>
            <p className="cta-note">Start earning passive income from your storybooks today</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <h2>Powerful Features</h2>
        <div className="features-grid">
          <div className="feature">
            <div className="feature-icon">🎨</div>
            <h3>AI-Powered Illustrations</h3>
            <p>Generate professional-quality character artwork and scenes using GPT-Image-1-Mini</p>
          </div>
          <div className="feature">
            <div className="feature-icon">✍️</div>
            <h3>Story Planning</h3>
            <p>AI-assisted plot development and narrative suggestions</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🎭</div>
            <h3>Character Consistency</h3>
            <p>Create characters that look the same across all pages</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🖱️</div>
            <h3>Visual Editor</h3>
            <p>Drag-and-drop interface for easy page design</p>
          </div>
          <div className="feature">
            <div className="feature-icon">📖</div>
            <h3>Multi-Page Books</h3>
            <p>Create storybooks with as many pages as you need</p>
          </div>
          <div className="feature">
            <div className="feature-icon">💾</div>
            <h3>Amazon KDP Export</h3>
            <p>One-click export in Amazon KDP's exact required format with proper print specifications</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2>Ready to Create Your Storybook?</h2>
        <p>Join thousands of creators bringing their stories to life</p>
        <div className="cta-buttons">
          {user ? (
            <button className="btn btn-primary btn-large" onClick={() => navigate('/dashboard')}>
              Start Creating
            </button>
          ) : (
            <>
              <button className="btn btn-primary btn-large" onClick={() => navigate('/register')}>
                Sign Up Free
              </button>
              <button className="btn btn-secondary btn-large" onClick={() => navigate('/help')}>
                Learn More
              </button>
            </>
          )}
        </div>
      </section>
      
      {/* Lightbox Modal for Gallery */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>×</button>
            <button className="lightbox-arrow lightbox-arrow-left" onClick={prevLightboxImage}>
              ‹
            </button>
            <img 
              src={lightboxImage} 
              alt={`Screenshot ${lightboxIndex + 1}`}
              className="lightbox-image"
            />
            <button className="lightbox-arrow lightbox-arrow-right" onClick={nextLightboxImage}>
              ›
            </button>
            <div className="lightbox-counter">
              {lightboxIndex + 1} / 12
            </div>
          </div>
        </div>
      )}

      {/* Screenshot Modal */}
      {selectedScreenshot && (
        <div className="screenshot-modal-overlay" onClick={closeModal}>
          <div className="screenshot-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <h2>{selectedScreenshot.title}</h2>
            <p className="modal-description">{selectedScreenshot.description}</p>
            <div className="screenshot-carousel">
              {currentImageIndex > 0 && (
                <button className="carousel-arrow carousel-arrow-left" onClick={prevImage}>
                  ‹
                </button>
              )}
              <div className="screenshot-container">
                <img 
                  src={selectedScreenshot.images[currentImageIndex]} 
                  alt={`${selectedScreenshot.title} - ${currentImageIndex + 1}`}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800"%3E%3Crect fill="%23f3f4f6" width="1200" height="800"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="24" fill="%239ca3af"%3EScreenshot coming soon%3C/text%3E%3C/svg%3E';
                  }}
                />
              </div>
              {currentImageIndex < selectedScreenshot.images.length - 1 && (
                <button className="carousel-arrow carousel-arrow-right" onClick={nextImage}>
                  ›
                </button>
              )}
            </div>
            {selectedScreenshot.images.length > 1 && (
              <div className="carousel-indicators">
                {selectedScreenshot.images.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-indicator ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                    aria-label={`Go to screenshot ${index + 1}`}
                  />
                ))}
              </div>
            )}
            {selectedScreenshot.images.length > 1 && (
              <div className="carousel-counter">
                {currentImageIndex + 1} / {selectedScreenshot.images.length}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Tiny Little Fox Studio</h4>
            <p>Create and publish your own storybooks with AI-powered tools</p>
          </div>
          <div className="footer-section">
            <h4>Product</h4>
            <ul>
              <li><a href="/how-it-works">How It Works</a></li>
              <li><a href="/features">Features</a></li>
              <li><a href="/register">Get Started</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Resources</h4>
            <ul>
              <li><a href="/help">Help & Support</a></li>
              <li><a href="https://kdp.amazon.com" target="_blank" rel="noopener noreferrer">Amazon KDP</a></li>
              <li><a href="/dashboard">Dashboard</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Legal</h4>
            <ul>
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/terms">Terms of Service</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Tiny Little Fox Studio. All rights reserved.</p>
          <p className="footer-disclaimer">
            AI-generated content may require review. Users retain full ownership of their created works.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
